import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  IconButton,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  CreditScore as CreditScoreIcon,
  PointOfSale as PointOfSaleIcon,
  History as HistoryIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";

const API_URL = 'http://localhost:4000/api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflow: 'auto'
};

// Função para formatar moeda
const formatCurrency = (value) => {
  const numValue = Number(value) || 0;
  return new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN',
    minimumFractionDigits: 2,
  }).format(numValue).replace('MZN', 'MT');
};

// Função para garantir números seguros
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

// Função para calcular totais de forma segura
const calculateTotal = (vendas) => {
  if (!vendas || !Array.isArray(vendas)) return 0;
  
  return vendas.reduce((total, venda) => {
    const vendaTotal = safeNumber(venda.total, 0);
    return total + vendaTotal;
  }, 0);
};

// Função para contar produtos vendidos
const calculateTotalProdutosVendidos = (vendas) => {
  if (!vendas || !Array.isArray(vendas)) return 0;
  
  return vendas.reduce((total, venda) => {
    const itens = venda.itens || [];
    const totalItens = itens.reduce((sum, item) => {
      return sum + safeNumber(item.quantidade, 0);
    }, 0);
    return total + totalItens;
  }, 0);
};

const VendaRapidaView = ({ venda, formatCurrency, clientes }) => {
  const [open, setOpen] = useState(false);
  const cliente = clientes.find(c => c.id === venda.clienteId);

  return (
    <>
      <IconButton 
        size="small"
        onClick={() => setOpen(true)}
        title="Ver Detalhes"
        color="info">
        <HistoryIcon />
      </IconButton>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{...modalStyle, width: 500}}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon color="primary" />
            <Typography variant="h6" component="div">
              Venda #{venda.id}
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Cliente:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {cliente?.nome || 'Avulso'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Data:</Typography>
              <Typography variant="body1">{venda.data}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Total:</Typography>
              <Typography variant="h6" color="primary.main">
                {formatCurrency(venda.total || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Pagamento:</Typography>
              <Chip 
                label={venda.tipoPagamento} 
                color={venda.tipoPagamento === 'credito' ? 'warning' : 'success'}
                size="small"
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Itens:</Typography>
          <List dense>
            {(venda.itens || []).map((item, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText 
                  primary={`${item.nome} (${safeNumber(item.quantidade, 0)}x)`}
                  secondary={formatCurrency(safeNumber(item.precoVenda, 0) * safeNumber(item.quantidade, 0))}
                />
              </ListItem>
            ))}
          </List>
          <Button 
            fullWidth 
            variant="outlined" 
            onClick={() => setOpen(false)}
            sx={{ mt: 2 }}>
            Fechar
          </Button>
        </Box>
      </Modal>
    </>
  );
};

export default function Entradas() {

  const [clientes, setClientes] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openVendaRapida, setOpenVendaRapida] = useState(false);
  const [carrinho, setCarrinho] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState("");
  const [clienteSelecionado, setClienteSelecionado] = useState("");
  const [tipoPagamento, setTipoPagamento] = useState("dinheiro");
  const [valorPago, setValorPago] = useState(0);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [busca, setBusca] = useState('');
  
  // Estados para controle do pagamento parcial
  const [openConfirmacaoDivida, setOpenConfirmacaoDivida] = useState(false);
  const [dadosVendaPendente, setDadosVendaPendente] = useState(null);
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      console.log('🔄 Iniciando carregamento de dados...');
      
      const [clientesRes, produtosRes, vendasRes] = await Promise.all([
        fetch(`${API_URL}/clientes`),
        fetch(`${API_URL}/produtos`),
        fetch(`${API_URL}/vendas`)
      ]);

      // Verificar se as respostas são válidas
      if (!clientesRes.ok || !produtosRes.ok || !vendasRes.ok) {
        throw new Error('Erro ao carregar dados da API');
      }

      const clientesData = await clientesRes.json();
      const produtosData = await produtosRes.json();
      const vendasData = await vendasRes.json();

      let clientesArray = [];
      let produtosArray = [];
      let vendasArray = [];

      // Extração de dados mais robusta
      if (clientesData && clientesData.success && Array.isArray(clientesData.data)) {
        clientesArray = clientesData.data;
      } else if (Array.isArray(clientesData)) {
        clientesArray = clientesData;
      } else {
        clientesArray = [];
      }

      if (produtosData && produtosData.success && Array.isArray(produtosData.data)) {
        produtosArray = produtosData.data;
      } else if (Array.isArray(produtosData)) {
        produtosArray = produtosData;
      } else {
        produtosArray = [];
      }

      if (vendasData && vendasData.success && Array.isArray(vendasData.data)) {
        vendasArray = vendasData.data;
      } else if (Array.isArray(vendasData)) {
        vendasArray = vendasData;
      } else {
        vendasArray = [];
      }

      setClientes(clientesArray);
      setProdutos(produtosArray);
      setVendas(vendasArray);

    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      showSnackbar('Erro ao carregar dados', 'error');
      setClientes([]);
      setProdutos([]);
      setVendas([]);
    } finally {
      setLoading(false);
    }
  };

  const criarVenda = async (vendaData) => {
    console.log('🚀 Criando nova venda:', vendaData);

    try {
      const response = await fetch(`${API_URL}/vendas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vendaData),
      });

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        throw new Error(result.message || 'Erro ao criar venda');
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const adicionarAoCarrinho = (produto) => {
    const itemExistente = carrinho.find(item => item.produtoId === produto.id);
    
    if (itemExistente) {
      setCarrinho(carrinho.map(item =>
        item.produtoId === produto.id 
          ? { 
              ...item, 
              quantidade: safeNumber(item.quantidade, 0) + 1,
              precoVenda: safeNumber(item.precoVenda, 0)
            }
          : item
      ));
    } else {
      setCarrinho([...carrinho, { 
        produtoId: produto.id,
        codigo: produto.codigo,
        nome: produto.nome,
        precoVenda: safeNumber(produto.precoVenda, 0),
        quantidade: 1
      }]);
    }
    setBuscaProduto("");
  };

  const removerDoCarrinho = (produtoId) => {
    setCarrinho(carrinho.filter(item => item.produtoId !== produtoId));
  };

  const atualizarQuantidade = (produtoId, novaQuantidade) => {
    const novaQuant = safeNumber(novaQuantidade, 0);
    
    if (novaQuant < 1) {
      removerDoCarrinho(produtoId);
      return;
    }

    const produto = produtos.find(p => p.id === produtoId);
    if (novaQuant > safeNumber(produto?.quantidade, 0)) {
      showSnackbar(`Stock insuficiente! Disponível: ${safeNumber(produto?.quantidade, 0)}`, 'warning');
      return;
    }

    setCarrinho(carrinho.map(item =>
      item.produtoId === produtoId 
        ? { 
            ...item, 
            quantidade: novaQuant,
            precoVenda: safeNumber(item.precoVenda, 0)
          }
        : item
    ));
  };

  // Cálculo do subtotal
  const subtotal = carrinho.reduce((acc, item) => {
    const preco = safeNumber(item.precoVenda, 0);
    const quantidade = safeNumber(item.quantidade, 0);
    return acc + (preco * quantidade);
  }, 0);

  const totalVenda = subtotal;
  const valorPagoNum = safeNumber(valorPago, 0);
  const troco = Math.max(0, valorPagoNum - totalVenda);
  const valorFaltante = Math.max(0, totalVenda - valorPagoNum);

  const cliente = clientes.find(c => c.id === clienteSelecionado);

  const prepararDadosVenda = (criarDivida = false) => {
    // Preparar os itens com valores numéricos garantidos
    const itensValidados = carrinho.map(item => ({
      produtoId: item.produtoId,
      codigo: item.codigo,
      nome: item.nome,
      precoVenda: safeNumber(item.precoVenda, 0),
      quantidade: safeNumber(item.quantidade, 0),
      subtotal: safeNumber(item.precoVenda, 0) * safeNumber(item.quantidade, 0)
    }));

    const vendaData = {
      clienteId: clienteSelecionado,
      itens: itensValidados,
      tipoPagamento: tipoPagamento,
      formaPagamento: tipoPagamento === 'dinheiro' ? 'dinheiro' : 'credito',
      subtotal: safeNumber(subtotal, 0),
      total: safeNumber(totalVenda, 0),
      valorPago: tipoPagamento === 'dinheiro' ? valorPagoNum : 0,
      troco: tipoPagamento === 'dinheiro' ? Math.max(safeNumber(troco, 0), 0) : 0,
      divida: criarDivida ? safeNumber(valorFaltante, 0) : 0,
      observacoes: criarDivida ? `Pagamento parcial. Faltam ${formatCurrency(valorFaltante)}` : '',
      data: new Date().toISOString().split('T')[0],
      status: criarDivida ? 'parcial' : 'pago'
    };

    return vendaData;
  };

  const processarVenda = async (criarDivida = false) => {
    try {
      const vendaData = prepararDadosVenda(criarDivida);
      console.log('💰 Dados validados da venda:', vendaData);

      const result = await criarVenda(vendaData);

      if (result.success) {
        await fetchData();
        
        setCarrinho([]);
        setClienteSelecionado("");
        setTipoPagamento("dinheiro");
        setValorPago(0);
        setOpenVendaRapida(false);
        setOpenConfirmacaoDivida(false);

        if (criarDivida) {
          showSnackbar(`✅ Venda registrada com pagamento parcial! Dívida: ${formatCurrency(valorFaltante)}`, 'warning');
        } else {
          showSnackbar(`✅ Venda finalizada! Total: ${formatCurrency(totalVenda)}`);
        }
      }
    } catch (error) {
      console.error('Erro ao finalizar venda:', error);
      showSnackbar(error.message || 'Erro ao finalizar venda', 'error');
    }
  };

  const finalizarVenda = async () => {
    if (carrinho.length === 0) {
      showSnackbar("Adicione produtos ao carrinho!", 'warning');
      return;
    }

    if (!clienteSelecionado) {
      showSnackbar("Selecione um cliente!", 'warning');
      return;
    }

    // Para pagamento em dinheiro com valor insuficiente
    if (tipoPagamento === 'dinheiro' && valorPagoNum < totalVenda) {
      // Perguntar se deseja criar dívida
      setDadosVendaPendente({
        total: totalVenda,
        valorPago: valorPagoNum,
        valorFaltante: valorFaltante
      });
      setOpenConfirmacaoDivida(true);
      return;
    }

    // Se chegou aqui, é pagamento completo ou crédito
    await processarVenda(false);
  };

  const confirmarDivida = async () => {
    await processarVenda(true);
  };

  const cancelarDivida = () => {
    setOpenConfirmacaoDivida(false);
    setDadosVendaPendente(null);
    showSnackbar('Venda cancelada. Ajuste o valor pago ou os itens do carrinho.', 'info');
  };

  // Cálculos das estatísticas
  const totalVendas = Array.isArray(vendas) ? vendas.length : 0;
  const vendasCredito = Array.isArray(vendas) ? vendas.filter(v => v.tipoPagamento === 'credito').length : 0;
  const totalArrecadado = calculateTotal(vendas);
  const totalProdutosVendidos = calculateTotalProdutosVendidos(vendas);

  const produtosFiltrados = produtos.filter(produto =>
    produto.nome?.toLowerCase().includes(buscaProduto.toLowerCase()) ||
    produto.codigo?.toLowerCase().includes(buscaProduto.toLowerCase())
  );

  const vendasFiltradas = vendas.filter(venda => {
    const cliente = clientes.find(c => c.id === venda.clienteId);
    const matchBusca = cliente?.nome?.toLowerCase().includes(busca.toLowerCase()) ||
                      venda.id?.toString().includes(busca);
    
    const matchTipo = filtroTipo === 'todos' || venda.tipoPagamento === filtroTipo;

    return matchBusca && matchTipo;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PointOfSaleIcon fontSize="large" color="primary" />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Sistema de Vendas
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<PointOfSaleIcon />}
          onClick={() => setOpenVendaRapida(true)}
          size="large"
          sx={{ px: 4 }}
        >
          Venda Rápida
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReceiptIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Total Vendas
              </Typography>
              <Typography variant="h4" component="div">
                {totalVendas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CreditScoreIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Vendas a Crédito
              </Typography>
              <Typography variant="h4" component="div" color="warning.main">
                {vendasCredito}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <MoneyIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Total Arrecadado
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {formatCurrency(totalArrecadado)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ShoppingCartIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="textSecondary" gutterBottom>
                Produtos Vendidos
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {totalProdutosVendidos}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FILTROS */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Buscar venda..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              size="small"
              placeholder="Buscar por cliente ou ID"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Venda</InputLabel>
              <Select
                value={filtroTipo}
                label="Tipo de Venda"
                onChange={(e) => setFiltroTipo(e.target.value)}
              >
                <MenuItem value="todos">Todas as Vendas</MenuItem>
                <MenuItem value="dinheiro">À Vista</MenuItem>
                <MenuItem value="credito">A Crédito</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Histórico de Vendas ({vendasFiltradas.length})
          </Typography>
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Cliente</strong></TableCell>
              <TableCell><strong>Data</strong></TableCell>
              <TableCell><strong>Itens</strong></TableCell>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell><strong>Pagamento</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {vendasFiltradas.map((venda) => {
              const cliente = clientes.find(c => c.id === venda.clienteId);
              const totalItens = (venda.itens || []).reduce((acc, item) => acc + safeNumber(item.quantidade, 0), 0);

              return (
                <TableRow key={venda.id} hover>
                  <TableCell>#{venda.id}</TableCell>
                  <TableCell>{cliente?.nome || 'Avulso'}</TableCell>
                  <TableCell>{venda.data}</TableCell>
                  <TableCell>{totalItens} item(s)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(venda.total || 0)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={venda.tipoPagamento} 
                      size="small"
                      color={venda.tipoPagamento === 'credito' ? 'warning' : 'success'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <VendaRapidaView 
                      venda={venda} 
                      formatCurrency={formatCurrency}
                      clientes={clientes}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {vendasFiltradas.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhuma venda encontrada.
          </Alert>
        )}
      </Paper>

      {/* Modal de Venda Rápida */}
      <Dialog 
        open={openVendaRapida} 
        onClose={() => setOpenVendaRapida(false)} 
        maxWidth="lg" 
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PointOfSaleIcon />
            <Typography variant="h6" component="div">
              Venda Rápida
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ height: '100%' }}>
            <Grid item xs={7}>
              <TextField
                fullWidth
                label="Buscar produto..."
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Digite nome ou código do produto"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
              />

              <Box sx={{ height: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {produtosFiltrados.map(produto => (
                  <ListItem
                    key={produto.id}
                    button
                    onClick={() => adicionarAoCarrinho(produto)}
                    sx={{ 
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" fontWeight="medium">
                            {produto.nome}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary.main">
                            {formatCurrency(produto.precoVenda || 0)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Código: {produto.codigo}</Typography>
                          <Typography 
                            variant="body2" 
                            color={safeNumber(produto.quantidade, 0) > 0 ? 'success.main' : 'error.main'}
                          >
                            Stock: {produto.quantidade || 0}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </Box>
            </Grid>

            <Grid item xs={5}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={clienteSelecionado}
                  label="Cliente"
                  onChange={(e) => setClienteSelecionado(e.target.value)}
                >
                  <MenuItem value="">Cliente Avulso</MenuItem>
                  {clientes.map(cliente => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de Pagamento</InputLabel>
                <Select
                  value={tipoPagamento}
                  label="Tipo de Pagamento"
                  onChange={(e) => {
                    setTipoPagamento(e.target.value);
                    if (e.target.value === 'credito') setValorPago(0);
                  }}
                >
                  <MenuItem value="dinheiro">Dinheiro</MenuItem>
                  <MenuItem value="credito">Crédito</MenuItem>
                </Select>
              </FormControl>

              {tipoPagamento === 'dinheiro' && (
                <TextField
                  fullWidth
                  label="Valor Pago"
                  type="number"
                  value={valorPago}
                  onChange={(e) => setValorPago(safeNumber(e.target.value, 0))}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">MT</InputAdornment>,
                  }}
                  error={valorPagoNum < totalVenda}
                  helperText={valorPagoNum < totalVenda ? `Valor insuficiente. Faltam ${formatCurrency(valorFaltante)}` : ''}
                />
              )}

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Badge badgeContent={carrinho.reduce((acc, item) => acc + safeNumber(item.quantidade, 0), 0)} color="primary">
                    <ShoppingCartIcon />
                  </Badge>
                  <Typography variant="h6" component="div">
                    Carrinho
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ height: 200, overflow: 'auto', mb: 2 }}>
                {carrinho.map(item => (
                  <Paper key={item.produtoId} sx={{ p: 1, mb: 1, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{item.nome}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatCurrency(item.precoVenda || 0)} × {item.quantidade || 0}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          onClick={() => atualizarQuantidade(item.produtoId, safeNumber(item.quantidade, 0) - 1)}
                        >
                          <RemoveIcon />
                        </IconButton>
                        
                        <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                          {item.quantidade || 0}
                        </Typography>
                        
                        <IconButton 
                          size="small" 
                          onClick={() => atualizarQuantidade(item.produtoId, safeNumber(item.quantidade, 0) + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                        
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => removerDoCarrinho(item.produtoId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))}
                {carrinho.length === 0 && (
                  <Alert severity="info">
                    Nenhum produto no carrinho
                  </Alert>
                )}
              </Box>
              <Paper sx={{ p: 2, backgroundColor: '#e8f5e8' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" component="div">Total:</Typography>
                  <Typography variant="h6" component="div" color="primary" fontWeight="bold">
                    {formatCurrency(totalVenda)}
                  </Typography>
                </Box>

                {tipoPagamento === 'dinheiro' && valorPagoNum > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="body2">Valor Pago:</Typography>
                      <Typography variant="body2">{formatCurrency(valorPago)}</Typography>
                    </Box>
                    {valorPagoNum >= totalVenda && troco > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Troco:</Typography>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {formatCurrency(troco)}
                        </Typography>
                      </Box>
                    )}
                    {valorPagoNum < totalVenda && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="error.main">Valor em falta:</Typography>
                        <Typography variant="body2" color="error.main" fontWeight="bold">
                          {formatCurrency(valorFaltante)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {tipoPagamento === 'credito' && cliente && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Venda a crédito: {formatCurrency(totalVenda)}
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setOpenVendaRapida(false)}
            size="large"
          >
            Cancelar
          </Button>
          
          <Box sx={{ flex: 1 }} />
          
          <Typography variant="h6" component="div" color="primary" sx={{ mr: 2 }}>
            Total: {formatCurrency(totalVenda)}
          </Typography>
          
          <Button 
            variant="contained"
            size="large"
            onClick={finalizarVenda}
            disabled={
              carrinho.length === 0 || 
              !clienteSelecionado
            }
            startIcon={<ReceiptIcon />}
            sx={{ px: 4 }}>
            Finalizar Venda
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Dívida */}
      <Dialog
        open={openConfirmacaoDivida}
        onClose={cancelarDivida}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditScoreIcon color="warning" />
            <Typography variant="h6" component="div">
              Pagamento Parcial
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Valor pago é insuficiente!
            </Typography>
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Total da venda: <strong>{formatCurrency(dadosVendaPendente?.total || 0)}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Valor pago: <strong>{formatCurrency(dadosVendaPendente?.valorPago || 0)}</strong>
            </Typography>
            <Typography variant="body2" color="error.main" fontWeight="bold">
              Valor em falta: <strong>{formatCurrency(dadosVendaPendente?.valorFaltante || 0)}</strong>
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Deseja registrar esta venda com pagamento parcial e criar uma dívida para o cliente?
          </Typography>

          <Alert severity="info">
            O cliente ficará com uma dívida de <strong>{formatCurrency(dadosVendaPendente?.valorFaltante || 0)}</strong> que poderá ser paga posteriormente.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarDivida} color="inherit">
            Cancelar Venda
          </Button>
          <Button 
            onClick={confirmarDivida} 
            variant="contained" 
            color="warning"
            startIcon={<CreditScoreIcon />}
          >
            Criar Dívida
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}